import {
  Bot,
  AlertTriangle,
  ArrowUpRight,
  ExternalLink,
  Server,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  getDiscordIconUrl,
  getDiscordAvatarUrl,
  getDiscordAppIconUrl,
  buildBotInviteUrl,
} from "@/utils/discord";
import { useDashboardMe } from "@/lib/api/dashboard";
import { useSubscription } from "@/lib/api/subscription";
import { createGuildAddIntent } from "@/lib/api/guilds";
import { useGuildAddedListener } from "@/hooks/use-guild-added-listener";
import type { ModalComponentProps } from "@/lib/modal";
import type { DiscordGuild, DashboardApplication } from "@/lib/api/types";

export interface AddBotModalProps {
  guild: DiscordGuild;
  applications: DashboardApplication[];
}

export function AddBotModal({
  guild,
  applications,
  onClose,
}: AddBotModalProps & ModalComponentProps) {
  const { data: meData } = useDashboardMe();

  const ownerTenant = meData?.data?.tenants.find((t) => t.role === "owner");
  const tenantId = ownerTenant?.id ?? "";

  const { data: subscriptionData } = useSubscription(tenantId);

  const currentGuilds = subscriptionData?.data?.usage.guilds ?? 0;
  const maxGuilds = subscriptionData?.data?.limits.maxGuildsPerApplication ?? 1;
  const isAtLimit = currentGuilds >= maxGuilds;

  useGuildAddedListener({ discordGuildId: guild.id, onSuccess: onClose });

  const handleAddBot = async (app: DashboardApplication) => {
    try {
      await createGuildAddIntent(app.id, guild.id);
    } catch {
      // Intent creation failed — fall back to manual claim
    }
    window.open(buildBotInviteUrl(app.discordClientId, guild.id), "_blank");
  };

  const getBotAvatarUrl = (app: DashboardApplication): string | undefined => {
    if (app.discordBotId && app.discordBotAvatar) {
      return getDiscordAvatarUrl(app.discordBotId, app.discordBotAvatar);
    }
    if (app.discordClientId && app.discordAppIcon) {
      return getDiscordAppIconUrl(app.discordClientId, app.discordAppIcon);
    }
    return undefined;
  };

  return (
    <>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle className="flex items-center gap-2">
          <Bot className="size-5 text-rose-500" />
          Add Bot
        </ResponsiveModalTitle>
        <ResponsiveModalDescription>
          Select a bot to add to this server.
        </ResponsiveModalDescription>
      </ResponsiveModalHeader>

      <div className="space-y-4 py-4">
        <div className="flex items-center gap-3 rounded-lg border p-3">
          <Avatar className="size-12">
            <AvatarImage
              src={getDiscordIconUrl(guild.id, guild.icon)}
              alt={guild.name}
            />
            <AvatarFallback className="text-sm">
              {guild.name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{guild.name}</p>
            <p className="text-muted-foreground text-sm">
              {guild.isOwner
                ? "You are the Discord owner"
                : "You have admin permissions"}
            </p>
          </div>
        </div>

        <div className="bg-muted/50 flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
          <span className="text-muted-foreground">Server Usage</span>
          <span className={isAtLimit ? "text-destructive font-medium" : ""}>
            {currentGuilds} / {maxGuilds}
          </span>
        </div>

        {isAtLimit && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
              <div className="text-sm">
                <p className="font-medium text-amber-600 dark:text-amber-400">
                  Server limit reached
                </p>
                <p className="text-muted-foreground mt-1">
                  Upgrade your plan to add bots to more servers or remove an
                  existing server first.
                </p>
                {ownerTenant && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2 h-auto p-0 text-amber-600 dark:text-amber-400"
                    asChild
                  >
                    <Link
                      to="/dashboard/teams/$tenantId"
                      params={{ tenantId: ownerTenant.id }}
                      search={{ tab: "subscription" }}
                    >
                      View subscription
                      <ArrowUpRight className="ml-1 size-3" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {applications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500/10 to-red-500/10">
              <Server className="size-5 text-rose-500" />
            </div>
            <p className="text-muted-foreground text-sm">
              No applications available. Create an application first.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {applications.map((app) => (
              <div
                key={app.id}
                className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-rose-500/5"
              >
                <Avatar className="size-10">
                  <AvatarImage
                    src={getBotAvatarUrl(app)}
                    alt={app.discordBotUsername ?? app.name}
                  />
                  <AvatarFallback className="text-xs">
                    {(app.discordBotUsername ?? app.name)
                      .slice(0, 2)
                      .toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">
                      {app.discordBotUsername ?? app.name}
                    </p>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {app.guilds.length}{" "}
                      {app.guilds.length === 1 ? "server" : "servers"}
                    </Badge>
                  </div>
                  {app.discordAppDescription && (
                    <p className="text-muted-foreground mt-0.5 truncate text-xs">
                      {app.discordAppDescription}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  className="shrink-0 gap-1.5"
                  disabled={isAtLimit}
                  onClick={() => handleAddBot(app)}
                >
                  <ExternalLink className="size-3.5" />
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ResponsiveModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </ResponsiveModalFooter>
    </>
  );
}

AddBotModal.contentClassName = "sm:max-w-md";
AddBotModal.displayName = "AddBotModal";
